'use client';

import { useInView } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRef } from 'react';

import type { BlogPostCardProps } from './blogTypes';

export default function FeaturedBlogPostsClient({
  posts,
}: {
  posts: BlogPostCardProps[];
}) {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.1 });

  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={containerRef}
          className="grid grid-cols-1 gap-8 opacity-0 transition-opacity duration-700 ease-in-out md:grid-cols-2"
          style={{
            opacity: isInView ? 1 : 0,
          }}
        >
          {posts.map((post) => (
            <Link
              href={`/blogs/${post.slug}/`}
              aria-label={post.title}
              key={post.id}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
            >
              <div className="relative aspect-video h-56 overflow-hidden">
                <div className="size-full">
                  <Image
                    src={post.feature_image || '/placeholder.jpg'}
                    alt={post.title}
                    fill
                    style={{ objectFit: 'cover' }}
                    className="object-center transition-transform duration-500 group-hover:scale-105"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </div>

                <div className="absolute right-4 top-4 z-10">
                  <span className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 shadow-sm ring-1 ring-inset ring-primary-600/20 backdrop-blur-sm transition-transform duration-200 hover:scale-105">
                    {post.tags[0]?.name || 'Uncategorized'}
                  </span>
                </div>
              </div>

              <div className="flex grow flex-col p-6">
                <div className="mb-3 flex items-center text-xs text-gray-500">
                  <svg
                    className="mr-1.5 size-3.5 text-primary-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1z" />
                  </svg>
                  {post.updated_at}
                  <span className="mx-1.5">•</span>
                  <span className="truncate font-medium">
                    {post.authors?.[0]?.name || 'Unknown'}
                  </span>
                </div>

                <h3 className="mb-3 text-left text-xl font-bold text-gray-900 transition-colors duration-200 group-hover:text-primary-600">
                  <div
                    // href={`/blogs/${post.slug}`}
                    className="inline-block"
                  >
                    {post.title}
                  </div>
                </h3>

                <p className="mb-5 line-clamp-2 text-left text-sm leading-relaxed text-gray-600 transition-colors duration-200 group-hover:text-gray-700">
                  {post.excerpt}
                </p>

                {/* <div className="mt-auto">
                  <Link
                    href={`/blogs/${post.slug}/`}
                    className="inline-flex items-center text-sm font-medium text-primary-600 transition-colors duration-200 hover:text-primary-700"
                  >
                    Read more
                    <svg
                      className="ml-1.5 size-4 transition-transform duration-200 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </Link>
                </div> */}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
